// @flow

// Vendors
import React, { useState, useEffect } from 'react';
import { createBrowserHistory } from 'history';
import url from 'url';

// Requests
import {
  fetchProjects,
  fetchMarkets,
  fetchIndustrialSectors
} from '../../../requests';

// Hooks
import { useTranslate } from '../../hooks';

// Helpers
import { geURLParameters } from '../../../lib/helpers';

// Constants
import { WL_CURRENT_THEME } from '../../../config';

// Components
import { ProjectsFilters, ProjectsGrid, ProjectsCategoryGrid } from '.';
import { Loading, ItemsEmpty } from '../../components';

const history = createBrowserHistory();

const Projects = () => {
  const categoryPageData = window.category_data;
  const queryParams = geURLParameters();
  const searchMinLength = 3;
  const initialValues = {
    search: queryParams.search || '',
    category: (categoryPageData || {}).id || queryParams.category || '',
    pageNumber: queryParams.pageNumber || 1,
    postsPerPage: queryParams.postsPerPage || 6
  };
  const [values, setValues] = useState(initialValues);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [hasLoadMore, setHasLoadMore] = useState(false);
  const showProjects =
    (values.search &&
      values.search.length !== 0 &&
      values.search.length > searchMinLength) ||
    values.category;
  const showEmpty = showProjects && !isLoading && projects.length < 1;
  const content = useTranslate({
    projectItemHoverText: 'View Project',
    projectsGridLoadMoreLabel: 'Show More Projects',
    categoryItemHoverText: 'View Projects',
    categoryGridLoadMoreLabel: 'Show All Categories',
    emptyItemsTitle: 'No Matching Projects.',
    emptyItemsMessage:
      "Your search didn't return any results. Please adjust the filters to see some projects.",
    emptyItemsActionLabel: 'Clear Filters'
  });

  const getProjects = values => {
    setLoading(true);
    fetchProjects(values)
      .then(({ hits, hits_count, hits_count_total }) => {
        setLoading(false);
        setProjects(hits);
        setHasLoadMore(hits_count < hits_count_total);
      })
      .catch(thrown => {
        setLoading(false);
        // console.error('Request canceled:', thrown.message);
      });
  };

  const getCategories = values => {
    setLoading(true);
    const categoryRequest =
      WL_CURRENT_THEME === 'flintco' ? fetchMarkets : fetchIndustrialSectors;

    categoryRequest(values).then(response => {
      setLoading(false);
      setCategories(response.hits);
    });
  };

  useEffect(() => {
    if (!categoryPageData) getCategories(values);
    if (initialValues.category || initialValues.search) getProjects(values);
  }, []);

  const applyFilters = values => getProjects(values);

  const setFilters = (newValues, options) => {
    const combinedValues = { ...values, ...newValues };
    const combinedOptions = {
      resetPageNumber: true,
      preventApplyFilters: false,
      ...options
    };

    if (combinedOptions.resetPageNumber) combinedValues.pageNumber = 1;
    if (!combinedOptions.preventApplyFilters) applyFilters(combinedValues);

    setValues(combinedValues);
    history.replace({ search: url.format({ query: combinedValues }) });
  };

  const setFilter = (name, value = '', options) =>
    setFilters({ [name]: value }, options);

  const resetFilters = () =>
    setFilters({
      ...initialValues,
      market: (categoryPageData || {}).id || '',
      search: ''
    });

  const resetFilter = name => setFilter(name);

  const loadMoreProjects = () =>
    setFilter('pageNumber', values.pageNumber + 1, { resetPageNumber: false });

  const handleInputChange = event => {
    if (event) event.preventDefault();
    const { name, value } = event.target;

    if (
      name === 'search' &&
      value.length !== 0 &&
      value.length < searchMinLength
    )
      return setFilter(name, value, { preventApplyFilters: true });

    return setFilter(name, value);
  };

  const handleSubmit = event => {
    if (event) event.preventDefault();
    applyFilters(values);
  };

  return (
    <>
      <ProjectsFilters
        categories={categories}
        values={values}
        handleSubmit={handleSubmit}
        handleInputChange={handleInputChange}
        resetFilter={resetFilter}
        showProjects={showProjects}
        categoryPageData={categoryPageData}
        searchButtonLabel={content.searchButtonLabel}
      />

      {showProjects && !showEmpty && (
        <ProjectsGrid
          projects={projects}
          loadMoreProjects={loadMoreProjects}
          hasLoadMore={hasLoadMore}
          itemHoverText={content.projectItemHoverText}
          loadMoreLabel={content.projectsGridLoadMoreLabel}
        />
      )}

      {!showProjects && (
        <ProjectsCategoryGrid
          categories={categories}
          setFilter={setFilter}
          itemHoverText={content.categoryItemHoverText}
          loadMoreLabel={content.categoryGridLoadMoreLabel}
        />
      )}

      {showEmpty && (
        <ItemsEmpty
          title={content.emptyItemsTitle}
          message={content.emptyItemsMessage}
          actionLabel={content.emptyItemsActionLabel}
          actionOnClick={resetFilters}
        />
      )}

      {isLoading && <Loading />}
    </>
  );
};

export default Projects;
